/**
 * A5 v2 competitor CONTENT analysis — shared Zod schemas + TS types.
 * Design: docs/features/a5-competitor.md (provenance: docs/archive/progress-docs-v2/a5-competitor-content-analysis-redesign.md).
 *
 * These schemas define the API-facing contract for the analysis surface and
 * are also used to validate LLM outputs (extract, cluster_label, reduce,
 * critique). Downstream A3 briefs pin to `CompetitorAnalysisStrategySchema`.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';

// ── enums ─────────────────────────────────────────────────────────────────
export const CompetitorAnalysisStatusSchema = z.enum([
  'queued',
  'discovering',
  'fetching',
  'extracting',
  'embedding',
  'clustering',
  'scoring',
  'synthesizing',
  'critiquing',
  'completed',
  'failed',
  'cancelled',
]);
export type CompetitorAnalysisStatus = z.infer<typeof CompetitorAnalysisStatusSchema>;

export const CompetitorPageTypeSchema = z.enum([
  'article',
  'product',
  'category',
  'forum',
  'video',
  'doc',
  'other',
]);
export type CompetitorPageType = z.infer<typeof CompetitorPageTypeSchema>;

export const CompetitorClusterClassificationSchema = z.enum([
  'must_have',
  'gap',
  'differentiator',
  'anti_pattern',
]);
export type CompetitorClusterClassification = z.infer<typeof CompetitorClusterClassificationSchema>;

export const CompetitorGapTypeSchema = z.enum([
  'topic',
  'question',
  'eeat',
  'media',
  'information_gain',
]);
export type CompetitorGapType = z.infer<typeof CompetitorGapTypeSchema>;

// ── scope + creation ──────────────────────────────────────────────────────
export const CompetitorAnalysisScopeSchema = z.object({
  top_n: z.number().int().min(3).max(30).default(10),
  include_page_types: z.array(CompetitorPageTypeSchema).optional(),
  exclude_domains: z.array(z.string()).max(50).optional(),
  render_js: z.enum(['auto', 'always', 'never']).default('auto'),
});
export type CompetitorAnalysisScope = z.infer<typeof CompetitorAnalysisScopeSchema>;

export const CreateCompetitorAnalysisRequestSchema = z
  .object({
    project_id: UuidSchema,
    keyword: z.string().min(2).max(200),
    /**
     * KeywordPicker refactor — set when the analysis was created from a
     * picker selection (rather than free text). When set, `keyword_run_id`
     * must also be set (refined below). Server-side integrity check verifies
     * the keyword belongs to the caller's tenant + project.
     */
    keyword_id: UuidSchema.optional(),
    keyword_run_id: UuidSchema.optional(),
    language: z.string().min(2).max(10).default('en'),
    country: z.string().min(2).max(3).default('US'),
    scope: CompetitorAnalysisScopeSchema.partial().optional(),
  })
  .refine((v) => !v.keyword_id || v.keyword_run_id, {
    message: 'keyword_run_id required when keyword_id is provided',
    path: ['keyword_run_id'],
  });
export type CreateCompetitorAnalysisRequest = z.infer<
  typeof CreateCompetitorAnalysisRequestSchema
>;

// ── SERP snapshot ─────────────────────────────────────────────────────────
export const SerpFeatureSchema = z.object({
  type: z.enum([
    'featured_snippet',
    'paa',
    'video_pack',
    'image_pack',
    'knowledge_panel',
    'ai_overview',
    'top_stories',
    'shopping',
    'local_pack',
    'twitter_carousel',
  ]),
  position: z.number().int().nullable(),
  owner_url: z.string().nullable(),
  content: z.string().nullable(),
  format: z.string().nullable(),
});
export type SerpFeature = z.infer<typeof SerpFeatureSchema>;

export const PaaQuestionSchema = z.object({
  question: z.string(),
  answer_snippet: z.string().nullable(),
  answer_url: z.string().nullable(),
});
export type PaaQuestion = z.infer<typeof PaaQuestionSchema>;

export const SerpOrganicResultSchema = z.object({
  rank: z.number().int().min(1),
  url: z.string(),
  display_url: z.string().nullable().optional(),
  domain: z.string(),
  title: z.string(),
  snippet: z.string().nullable().optional(),
});
export type SerpOrganicResult = z.infer<typeof SerpOrganicResultSchema>;

export const SerpSnapshotSchema = z.object({
  source: z.enum(['dataforseo', 'serper', 'reuse_a2']),
  fetched_at: IsoDateTimeSchema,
  results: z.array(SerpOrganicResultSchema),
  features: z.array(SerpFeatureSchema).default([]),
  paa: z.array(PaaQuestionSchema).default([]),
  related_searches: z.array(z.string()).default([]),
});
export type SerpSnapshot = z.infer<typeof SerpSnapshotSchema>;

// ── heading tree ──────────────────────────────────────────────────────────
export const HeadingNodeSchema: z.ZodType<{
  level: number;
  text: string;
  word_count: number;
  children: unknown[];
}> = z.lazy(() =>
  z.object({
    level: z.number().int().min(1).max(6),
    text: z.string(),
    word_count: z.number().int().min(0),
    children: z.array(HeadingNodeSchema),
  }),
);
export type HeadingNode = z.infer<typeof HeadingNodeSchema>;

// ── EEAT scorecard ────────────────────────────────────────────────────────
export const EeatScorecardSchema = z.object({
  byline_present: z.boolean(),
  author_url_present: z.boolean(),
  org_schema: z.boolean(),
  about_page_linked: z.boolean(),
  published_at_present: z.boolean(),
  modified_at_present: z.boolean(),
  citation_density: z.number().min(0),
  authoritative_citation_density: z.number().min(0),
  first_person_score: z.number().min(0),
  https: z.boolean(),
  reviewer_byline: z.boolean(),
  score_0_100: z.number().int().min(0).max(100),
});
export type EeatScorecard = z.infer<typeof EeatScorecardSchema>;

// ── per-page scorecard (LLM reduce output) ────────────────────────────────
export const PageScorecardSchema = z.object({
  intent_match: z.number().int().min(0).max(100),
  structure: z.number().int().min(0).max(100),
  eeat: z.number().int().min(0).max(100),
  media_richness: z.number().int().min(0).max(100),
  freshness: z.number().int().min(0).max(100),
  unique_angle: z.number().int().min(0).max(100),
  overall: z.number().int().min(0).max(100),
});
export type PageScorecard = z.infer<typeof PageScorecardSchema>;

// ── LLM output schemas ────────────────────────────────────────────────────

/** `competitor.extract` (map step, one per included competitor). */
export const CompetitorExtractOutputSchema = z.object({
  dominant_topics: z.array(z.string()).min(1).max(8),
  unique_angle: z.string().min(10).max(500),
  first_person_evidence: z.array(z.string().max(300)).max(5),
  depth_evidence: z.array(z.string().max(300)).max(5),
  apparent_authorship_intent: z.string().min(10).max(300),
});
export type CompetitorExtractOutput = z.infer<typeof CompetitorExtractOutputSchema>;

/** `competitor.cluster_label` (one per cluster after HDBSCAN). Prefixed to
 * avoid the name collision with A2's keyword-cluster label schema. */
export const CompetitorClusterLabelOutputSchema = z.object({
  label: z.string().min(3).max(120),
  keywords: z.array(z.string()).min(1).max(10),
  top_questions: z.array(z.string()).max(5),
});
export type CompetitorClusterLabelOutput = z.infer<typeof CompetitorClusterLabelOutputSchema>;

/** `competitor.reduce` (rubric-guided synthesis; one per analysis). */
export const CompetitorReduceOutputSchema = z.object({
  serp_archetype: z.string().min(80).max(1200),
  per_page_scorecards: z.array(
    z.object({
      url: z.string(),
      scorecard: PageScorecardSchema,
      why_it_ranks: z.array(z.string().max(300)).min(1).max(5),
    }),
  ),
  patterns: z.object({
    median_word_count: z.number().int().min(0),
    median_h2_count: z.number().int().min(0),
    table_presence_rate: z.number().min(0).max(1),
    video_presence_rate: z.number().min(0).max(1),
    faq_presence_rate: z.number().min(0).max(1),
    format_distribution: z.record(z.number()).optional(),
    common_openings: z.string().max(600).optional(),
    shared_entities: z.array(z.string()).max(20).optional(),
  }),
  gaps: z.array(
    z.object({
      gap_type: CompetitorGapTypeSchema,
      title: z.string().min(4).max(200),
      detail: z.string().min(4).max(800),
      evidence: z.record(z.unknown()).optional(),
      // Required in the prompt; kept optional here so missing scores fail soft
      // into the worker heuristic rather than failing the whole reduce call.
      impact_score: z.number().int().min(0).max(100).optional(),
    }),
  ),
});
export type CompetitorReduceOutput = z.infer<typeof CompetitorReduceOutputSchema>;

const CompetitorGapDraftSchema = z.object({
  gap_type: CompetitorGapTypeSchema,
  title: z.string().min(4).max(200),
  detail: z.string().min(4).max(800),
  evidence: z.record(z.unknown()).optional(),
  // Prompt requires a score; optional here so critique can fail soft into heuristic.
  impact_score: z.number().int().min(0).max(100).optional(),
});

/** `competitor.critique` (senior-strategist rewrite; the A3-facing bundle). */
export const CompetitorCritiqueOutputSchema = z.object({
  serp_archetype: z.string().min(80).max(1200),
  recommended_angle: z.string().min(80).max(1500),
  outline_hints: z.array(
    z.object({
      h2: z.string().min(4).max(200),
      why: z.string().min(4).max(400),
      coverage_status: CompetitorClusterClassificationSchema,
      supporting_urls: z.array(z.string()).max(5).optional(),
    }),
  ),
  /** Optional re-ranked gaps with required impact_score. */
  gaps: z.array(CompetitorGapDraftSchema).optional(),
});
export type CompetitorCritiqueOutput = z.infer<typeof CompetitorCritiqueOutputSchema>;

// ── API-facing composite ──────────────────────────────────────────────────

export const CompetitorAnalysisSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  project_id: UuidSchema,
  status: CompetitorAnalysisStatusSchema,
  keyword: z.string(),
  /**
   * KeywordPicker refactor — populated when the analysis was created from a
   * picker selection. Null on pre-migration rows + free-text entries.
   */
  keyword_id: UuidSchema.nullable().optional(),
  keyword_run_id: UuidSchema.nullable().optional(),
  language: z.string(),
  country: z.string(),
  serp_source: z.string().nullable(),
  serp_archetype_summary: z.string().nullable(),
  recommended_angle: z.string().nullable(),
  outline_hints: z
    .array(
      z.object({
        h2: z.string(),
        why: z.string(),
        coverage_status: CompetitorClusterClassificationSchema,
        supporting_urls: z.array(z.string()).optional(),
      }),
    )
    .nullable(),
  patterns: z.record(z.unknown()).nullable(),
  competitors_included: z.number().int().nullable(),
  competitors_excluded: z.number().int().nullable(),
  clusters_found: z.number().int().nullable(),
  gaps_found: z.number().int().nullable(),
  warnings: z
    .array(
      z.object({
        code: z.string(),
        severity: z.enum(['info', 'warning']).optional(),
        message: z.string(),
        details: z.record(z.unknown()).optional(),
      }),
    )
    .default([]),
  progress: z.record(z.unknown()).optional(),
  started_at: IsoDateTimeSchema.nullable(),
  completed_at: IsoDateTimeSchema.nullable(),
  failed_at: IsoDateTimeSchema.nullable(),
  failure_reason: z.string().nullable(),
  created_at: IsoDateTimeSchema,
});
export type CompetitorAnalysis = z.infer<typeof CompetitorAnalysisSchema>;

export const CompetitorAnalysisPageSchema = z.object({
  id: UuidSchema,
  analysis_id: UuidSchema,
  rank: z.number().int(),
  url: z.string(),
  final_url: z.string().nullable(),
  domain: z.string(),
  page_type: CompetitorPageTypeSchema.nullable(),
  included_in_analysis: z.boolean(),
  exclusion_reason: z.string().nullable(),
  title: z.string().nullable(),
  meta_description: z.string().nullable(),
  canonical: z.string().nullable(),
  published_at: IsoDateTimeSchema.nullable(),
  modified_at: IsoDateTimeSchema.nullable(),
  author_name: z.string().nullable(),
  author_url: z.string().nullable(),
  word_count: z.number().int().nullable(),
  reading_time_seconds: z.number().int().nullable(),
  heading_tree: z.array(HeadingNodeSchema).nullable(),
  schema_types: z.array(z.string()).default([]),
  outbound_citations: z.number().int(),
  authoritative_citations: z.number().int(),
  images: z.number().int(),
  videos: z.number().int(),
  tables: z.number().int(),
  faqs: z.number().int(),
  code_blocks: z.number().int(),
  eeat: EeatScorecardSchema.nullable(),
  scorecard: PageScorecardSchema.nullable(),
  why_it_ranks: z.array(z.string()).nullable(),
  extract_llm: CompetitorExtractOutputSchema.nullable(),
  fetch_status_code: z.number().int().nullable(),
  fetch_error: z.string().nullable(),
});
export type CompetitorAnalysisPage = z.infer<typeof CompetitorAnalysisPageSchema>;

export const CompetitorAnalysisClusterSchema = z.object({
  id: UuidSchema,
  analysis_id: UuidSchema,
  label: z.string(),
  keywords: z.array(z.string()),
  coverage_rate: z.number().min(0).max(1),
  avg_depth_words: z.number().int(),
  classification: CompetitorClusterClassificationSchema,
  urls_covering: z.array(z.string()),
  top_questions: z.array(z.string()).nullable(),
});
export type CompetitorAnalysisCluster = z.infer<typeof CompetitorAnalysisClusterSchema>;

export const CompetitorAnalysisGapSchema = z.object({
  id: UuidSchema,
  analysis_id: UuidSchema,
  gap_type: CompetitorGapTypeSchema,
  title: z.string(),
  detail: z.string().nullable(),
  evidence: z.record(z.unknown()).nullable(),
  /** 0..100 calibrated impact; null only on legacy rows predating calibration. */
  impact_score: z.number().int().min(0).max(100).nullable(),
});
export type CompetitorAnalysisGap = z.infer<typeof CompetitorAnalysisGapSchema>;

/** A3-facing bundle — the load-bearing downstream contract. */
export const CompetitorAnalysisStrategySchema = z.object({
  analysis_id: UuidSchema,
  keyword: z.string(),
  language: z.string(),
  country: z.string(),
  serp_archetype: z.string(),
  recommended_angle: z.string(),
  outline_hints: z.array(
    z.object({
      h2: z.string(),
      why: z.string(),
      coverage_status: CompetitorClusterClassificationSchema,
      supporting_urls: z.array(z.string()).optional(),
    }),
  ),
  patterns: z.record(z.unknown()).optional(),
  gaps: z.array(CompetitorAnalysisGapSchema).default([]),
  must_have_clusters: z.array(CompetitorAnalysisClusterSchema).default([]),
});
export type CompetitorAnalysisStrategy = z.infer<typeof CompetitorAnalysisStrategySchema>;
